import {
    MissionsApi,
    Configuration as MissionsConfiguration,
} from '@control-center/control-center-client-public-missions-ts';
import { Configuration, VehiclesApi } from '@control-center/control-center-client-public-vehicles-ts';
import { fetchAuthSession } from 'aws-amplify/auth';
import type { RawAxiosRequestConfig } from 'axios';
import { v4 as uuid } from 'uuid';

const REQUEST_TIMEOUT_MS = 30_000;

/** One correlation id per request, for cross-service tracing. Shared by every generated client. */
export const correlationHeaders = (): RawAxiosRequestConfig => ({ headers: { 'x-correlation-id': uuid() } });

export class RestError extends Error {
    readonly status: number;
    readonly body: unknown;

    constructor(message: string, status: number, body?: unknown) {
        super(message);
        this.name = 'RestError';
        this.status = status;
        this.body = body;
    }
}

/** Shared by every generated REST client here: one Cognito access token per request. */
export const getServiceAccessToken = async (): Promise<string> => {
    const token = (await fetchAuthSession()).tokens?.accessToken?.toString();
    if (!token) {
        throw new RestError('No Cognito access token available', 401);
    }
    return token;
};

const configuration = new Configuration({
    basePath: import.meta.env.VITE_VEHICLE_SERVICE_ENDPOINT?.replace(/\/+$/, ''),
    accessToken: getServiceAccessToken,
    baseOptions: {
        timeout: REQUEST_TIMEOUT_MS,
        headers: { accept: 'application/json' },
    },
});

export const vehiclesApi = new VehiclesApi(configuration);

/**
 * Separate Configuration instance from the *missions* package, so the two clients keep
 * independent basePaths even though the two generated Configuration classes are identical.
 *
 * Only the configuration is passed — no basePath 2nd arg, no axios instance 3rd arg. Handing a
 * root axios instance to a generated client is what produces the pre-existing dual-axios type
 * errors in `src/features/domain-simulation/apiClient.ts`.
 */
const missionConfiguration = new MissionsConfiguration({
    basePath: import.meta.env.VITE_MISSION_HUB_SERVICE_ENDPOINT?.replace(/\/+$/, ''),
    accessToken: getServiceAccessToken,
    baseOptions: {
        timeout: REQUEST_TIMEOUT_MS,
        headers: { accept: 'application/json' },
    },
});

export const missionsApi = new MissionsApi(missionConfiguration);
